import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import getApiClient from "@/lib/api";
import { ApiError, unwrap } from "@/lib/api/error";
import type { components } from "@/lib/api.gen";

type EditUserRequest = components["schemas"]["EditUserRequest"];
type ChangePasswordRequest = components["schemas"]["ChangePasswordRequest"];
type NewPersonaRequest = components["schemas"]["NewPersonaRequest"];

export function useUserSummary() {
  const apiClient = getApiClient();
  return useQuery({
    queryKey: ["user-summary"],
    queryFn: async () => unwrap(await apiClient.GET("/api/user/summary")),
  });
}

/** Fetch another user's public profile fields. Lazy: only runs when `enabled`. */
export function useUserInfo(id: string, enabled = true) {
  const apiClient = getApiClient();
  return useQuery({
    queryKey: ["user-info", id],
    enabled: enabled && !!id,
    queryFn: async () =>
      unwrap(
        await apiClient.GET("/api/user/{id}", {
          params: { path: { id } },
        }),
      ),
  });
}

/**
 * Fetch a user's aggregated stats. Returns `null` when the user has not opted
 * to share their stats. Lazy: only runs when `enabled`.
 */
export function useUserStats(id: string, enabled = true) {
  const apiClient = getApiClient();
  return useQuery({
    queryKey: ["user-stats", id],
    enabled: enabled && !!id,
    queryFn: async () =>
      unwrap(
        await apiClient.GET("/api/user/{id}/stats", {
          params: { path: { id } },
        }),
      ),
  });
}

export function useUserJobInstances() {
  const apiClient = getApiClient();
  return useQuery({
    queryKey: ["user-job-instances"],
    queryFn: async () => unwrap(await apiClient.GET("/api/user/job-instances")),
  });
}

export function useEditUser(options?: { onSuccess?: () => void }) {
  const apiClient = getApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: EditUserRequest) => {
      unwrap(await apiClient.PATCH("/api/user", { body: payload }));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-summary"] });
      queryClient.invalidateQueries({ queryKey: ["email-preferences"] });
      queryClient.invalidateQueries({ queryKey: ["notification-preferences"] });
      options?.onSuccess?.();
    },
  });
}

export function useUserEmails() {
  const apiClient = getApiClient();
  return useQuery({
    queryKey: ["user-emails"],
    queryFn: async () => unwrap(await apiClient.GET("/api/user/emails")),
  });
}

export function useEditUserEmails(options?: { onSuccess?: () => void }) {
  const apiClient = getApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (emails: string[]) => {
      unwrap(await apiClient.PATCH("/api/user/emails", { body: { emails } }));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-emails"] });
      options?.onSuccess?.();
    },
  });
}

export function useVerifyEmail(options?: { onSuccess?: () => void }) {
  const apiClient = getApiClient();
  return useMutation({
    mutationFn: async (email: string) => {
      unwrap(
        await apiClient.POST("/api/user/emails/verify", { body: { email } }),
      );
    },
    onSuccess: () => {
      options?.onSuccess?.();
    },
  });
}

export function useChangePassword(options?: { onSuccess?: () => void }) {
  const apiClient = getApiClient();
  return useMutation({
    mutationFn: async (payload: ChangePasswordRequest) => {
      unwrap(await apiClient.POST("/api/user/password", { body: payload }));
    },
    onSuccess: () => {
      options?.onSuccess?.();
    },
  });
}

export function useLogout(options?: { onSuccess?: () => void }) {
  const apiClient = getApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      unwrap(await apiClient.POST("/api/user/logout"));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["user-summary"] });
      options?.onSuccess?.();
    },
  });
}

export function useEmailPreferences() {
  const apiClient = getApiClient();
  return useQuery({
    queryKey: ["email-preferences"],
    queryFn: async () =>
      unwrap(await apiClient.GET("/api/user/emails/preferences")),
  });
}

export function useNotificationPreferences() {
  const apiClient = getApiClient();
  return useQuery({
    queryKey: ["notification-preferences"],
    queryFn: async () =>
      unwrap(await apiClient.GET("/api/user/notification-preferences")),
  });
}

export function usePersonas() {
  const apiClient = getApiClient();
  return useQuery({
    queryKey: ["personas"],
    queryFn: async () => unwrap(await apiClient.GET("/api/user/personas")),
  });
}

export function useCreatePersona(options?: { onSuccess?: () => void }) {
  const apiClient = getApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: NewPersonaRequest) =>
      unwrap(await apiClient.POST("/api/user/persona/create", { body: payload })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["personas"] });
      queryClient.invalidateQueries({ queryKey: ["user-summary"] });
      options?.onSuccess?.();
    },
  });
}

export function useDeletePersona(options?: { onSuccess?: () => void }) {
  const apiClient = getApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (personaId: string) => {
      unwrap(
        await apiClient.DELETE("/api/user/persona/{persona_id}", {
          params: { path: { persona_id: personaId } },
        }),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["personas"] });
      queryClient.invalidateQueries({ queryKey: ["user-summary"] });
      options?.onSuccess?.();
    },
  });
}

export function useUploadImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      file,
      usage,
    }: {
      file: File;
      usage: "profile_picture" | "event_image";
    }) => {
      const formData = new FormData();
      formData.append("usage", JSON.stringify(usage));
      formData.append("file", file);

      const response = await fetch(
        `${window.location.origin}/api/file/image`,
        {
          method: "POST",
          body: formData,
          credentials: "include",
        },
      );
      if (!response.ok) {
        // Parse the backend's ApiError body so the global toast can translate
        // it (fall back to text for non-JSON responses).
        let body: unknown;
        try {
          body = await response.json();
        } catch {
          body = await response.text();
        }
        throw ApiError.from(body);
      }
      return (await response.json()) as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-summary"] });
    },
  });
}
